import { useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useAppearance } from '@/shared/appearance/AppearanceProvider'
import { BRAND_BY_THEME, BRAND_FACE } from '@/shared/branding/brand'
import { useI18n } from '@/shared/i18n/I18nProvider'
import type { MessageId } from '@/shared/i18n/catalogue.en'

/**
 * WHICH PAGE A TAB IS: the route's own answer, required of every route.
 *
 * A router that swaps the outlet without a document load leaves the tab saying
 * whatever the last real navigation left there — which, before this, was the
 * brand name on every address in the product. A clinician with a dozen
 * workspace tabs open could not tell one from another, and neither could the
 * browser's history or a restored session.
 *
 * IT IS DECLARED BY THE ROUTE, not resolved from the path. A second table
 * keyed by pathname would be a second opinion about the address space, and it
 * would be the one that rots: a route added, renamed or nested elsewhere
 * compiles either way, and only the tab quietly stops saying anything. Making
 * the field REQUIRED here is what closes that door — a new `createRoute` that
 * has not answered this does not compile.
 *
 * `null` is an answer, and the entry and the sign-in screen give it: the
 * product introducing itself IS the page there, so the tab carries the brand
 * alone rather than the brand twice.
 *
 * WHERE A DESTINATION TABLE ALREADY OWNS THE NAME, THE ROUTE READS IT FROM
 * THERE. The six primary destinations are named once in
 * `shared/workspace/workspaceDestinations.ts` and the tab says the same word
 * the tile does. The rest name a message directly, because no table owns them
 * at the granularity an address has: a section of the Schedule, of the Control
 * Center or of the Profile is a page of its area, and the area is what a tab
 * is for.
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    /** The message naming this address, or `null` for the brand alone. */
    title: MessageId | null
  }
}

/**
 * The tab, written once per page — mounted in the root layout, above the
 * session gate, so an address that ends at sign-in or at not-found still says
 * what it is.
 *
 * The BRAND half comes from the same `brand.ts` face the tab's icon does, so a
 * white-label's tab cannot end up wearing one product's mark and another's
 * name. The two are joined by a catalogue message rather than here: which mark
 * separates a page from a product, and which side of the tab each stands on,
 * is the translator's to decide.
 *
 * The DEEPEST match answers. Every route declares a title, and the pathless
 * layout routes that hold the session and capability gates declare `null`;
 * reading the last match is therefore reading the page rather than the area it
 * happens to be nested under.
 */
export function DocumentTitle() {
  const { t } = useI18n()
  const { appearance } = useAppearance()
  const page = useRouterState({ select: routeTitle })
  const brand = BRAND_FACE[BRAND_BY_THEME[appearance.theme]].title

  useEffect(() => {
    document.title = page === null ? brand : t('app.documentTitle', { page: t(page), brand })
  }, [brand, page, t])

  return null
}

/**
 * A PRIMITIVE, deliberately: the selector runs on every router state change —
 * a search parameter, a pending load, a panel recording that it is open — and
 * returning the message id rather than the match list is what keeps the tab
 * from re-rendering for any of them.
 *
 * A REFUSED ADDRESS WINS over the route it was refused on. `beforeLoad` throws
 * `notFound()` for a capability this account does not hold or a section that
 * does not exist, and the matches it threw past keep their own titles; a tab
 * reading "Schedule" over the not-found page would be the browser's history
 * recording a page this account never saw.
 *
 * `_notFound` is the router's own mark for "this match answered with the
 * not-found component", set on the match that OWNS the answer — the root,
 * which is where `notFoundComponent` is declared — and cleared on the next
 * navigation that resolves. Its underscore is the library's; it is declared on
 * the published match type, so a version that drops it fails the type check
 * rather than quietly leaving every tab saying the page is missing. The
 * match's `status` is not the question: a refused address leaves the root
 * `success` and the routes below it `pending`.
 *
 * The state is described STRUCTURALLY rather than by importing the router's
 * own `RouterState`, whose generics carry the whole route tree — this module
 * asks two fields of a match and should not have to name fourteen type
 * parameters to do it.
 */
function routeTitle(state: {
  matches: readonly { _notFound?: boolean; staticData: { title: MessageId | null } }[]
}) {
  if (state.matches.some((match) => match._notFound === true)) {
    return 'notFound.title' satisfies MessageId
  }
  return state.matches[state.matches.length - 1]?.staticData.title ?? null
}
